/**
 * Builds EMVCo P2M QR payload for BZU fee vouchers (Bank Alfalah / Raast).
 * Production MCC/terminal IDs from BAFL onboarding; QR String Pattern sheet was test-only.
 */

const BZU_QR_CONFIG = {
  guid: "9FC778F69B981FD18DC9297948000CFF",
  raastCode: "ALFHPKKA",
  mcc: "8220",
  currency: "586",
  countryCode: "PK",
  city: "Multan",
  morning: {
    iban: "PK55ALFH5767005001662255",
    merchantName: "BZ University Multan",
    terminalId: "545004",
  },
  evening: {
    iban: "PK86ALFH5767005002149898",
    merchantName: "BZ University Multan",
    terminalId: "544996",
  },
};

const formatTlv = (tag, value) => {
  const str = String(value);
  return `${tag}${String(str.length).padStart(2, "0")}${str}`;
};

const calculateCrc16 = (data) => {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i += 1) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j += 1) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff;
      } else {
        crc = (crc << 1) & 0xffff;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
};

const toKarachiParts = (dateValue) => {
  if (!dateValue) return null;

  const str = String(dateValue).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return {
      day: dateOnly[3],
      month: dateOnly[2],
      year: dateOnly[1].slice(-2),
    };
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Karachi",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    day: get("day"),
    month: get("month"),
    year: get("year"),
  };
};

/** Amount TLV value per BAFL samples (e.g. 5 -> "05", 36250 -> "36250"). */
const formatAmountValue = (amount) => {
  const rounded = Math.round(Number(amount) || 0);
  const str = String(rounded);
  return rounded < 10 ? str.padStart(2, "0") : str;
};

/** Bill number (tag 62-01): 10-digit challan */
const formatBillNumber = (challanNo) => {
  const digits = String(challanNo ?? "").replace(/\D/g, "");
  return digits.slice(-10).padStart(10, "0");
};

/**
 * Reference Id (tag 62-05): 00 + invoice(10) + billing month MMYY + due date DDMMYY
 */
export const buildBzuQrReferenceId = (challanNo, printDate, dueDate) => {
  const bill = formatBillNumber(challanNo);
  const issue = toKarachiParts(printDate);
  const due = toKarachiParts(dueDate);
  const billingMonth = issue ? `${issue.month}${issue.year}` : "0000";
  const dueDatePart = due ? `${due.day}${due.month}${due.year}` : "000000";
  return `00${bill}${billingMonth}${dueDatePart}`;
};

/** Terminal ID (tag 62-07): fixed per shift per BAFL spec */
export const buildBzuQrTerminalId = (shift = "morning") =>
  shift === "evening"
    ? BZU_QR_CONFIG.evening.terminalId
    : BZU_QR_CONFIG.morning.terminalId;

export const resolveBzuQrShift = (admissionVoucher = {}) => {
  const explicit = String(admissionVoucher.qr_shift || admissionVoucher.shift || "").toLowerCase();
  if (explicit.includes("evening")) return "evening";
  if (explicit.includes("morning")) return "morning";

  const session = String(admissionVoucher.session || "").toLowerCase();
  if (session.includes("evening")) return "evening";
  if (session.includes("morning")) return "morning";

  const programGroup = String(admissionVoucher.program_group || "").toUpperCase();
  if (programGroup === "EVENING" || programGroup.includes("EVENING")) return "evening";
  if (programGroup === "MORNING" || programGroup.includes("MORNING")) return "morning";

  const programGroupLower = programGroup.toLowerCase();
  if (programGroupLower.includes("evening")) return "evening";

  return "morning";
};

/**
 * @param {Object} params
 * @param {number|string} params.amount - Fee amount (PKR)
 * @param {string} params.challanNo - Voucher / challan number
 * @param {string|Date} [params.printDate] - Issue date (for billing month MMYY)
 * @param {string|Date} [params.dueDate] - Due date (for DDMMYY in reference id)
 * @param {'morning'|'evening'} [params.shift='morning']
 */
export const buildBzuVoucherQrString = ({
  amount,
  challanNo,
  printDate,
  dueDate,
  shift = "morning",
}) => {
  const shiftKey = shift === "evening" ? "evening" : "morning";
  const { iban, merchantName } = BZU_QR_CONFIG[shiftKey];

  const amountStr = formatAmountValue(amount);
  const billNumber = formatBillNumber(challanNo);
  const referenceId = buildBzuQrReferenceId(challanNo, printDate, dueDate);
  const terminalId = buildBzuQrTerminalId(shiftKey);

  const tag28Inner =
    formatTlv("00", BZU_QR_CONFIG.guid) +
    formatTlv("01", BZU_QR_CONFIG.raastCode) +
    formatTlv("02", iban);

  const tag62Inner =
    formatTlv("01", billNumber) +
    formatTlv("05", referenceId) +
    formatTlv("07", terminalId);

  const payload =
    formatTlv("00", "01") +
    formatTlv("01", "11") +
    formatTlv("28", tag28Inner) +
    formatTlv("52", BZU_QR_CONFIG.mcc) +
    formatTlv("53", BZU_QR_CONFIG.currency) +
    formatTlv("54", amountStr) +
    formatTlv("58", BZU_QR_CONFIG.countryCode) +
    formatTlv("59", merchantName) +
    formatTlv("60", BZU_QR_CONFIG.city) +
    formatTlv("62", tag62Inner);

  const crc = calculateCrc16(`${payload}6304`);
  return `${payload}${formatTlv("63", crc)}`;
};

export const buildBzuVoucherQrStringFromVoucher = (admissionVoucher = {}) =>
  buildBzuVoucherQrString({
    amount: admissionVoucher.amount,
    challanNo: admissionVoucher.challan_no,
    printDate: admissionVoucher.print_date,
    dueDate: admissionVoucher.due_date,
    shift: resolveBzuQrShift(admissionVoucher),
  });
